import { Suspense, lazy } from 'react'
import { Checkbox, Separator, useTheme } from 'tamagui'
import CheckIcon from '@mui/icons-material/Check'
import { CobuStack, CobuText, CobuButton } from '@cobu/ui'
import { Strings } from '../../constants'
import { useOnboarding } from '../../hooks/useOnboarding'
import { EventsIcon } from '../../assets/icons/EventsIcon'
import { ChatIcon } from '../../assets/icons/ChatIcon'
import { MarketplaceIcon } from '../../assets/icons/MarketplaceIcon'
import BrandBadge from '../BrandBadge'

// The five legal documents are ~80KB of text that most residents never expand, so
// they load with the modal rather than blocking the screen that gates the feed.
const TermsModal = lazy(() => import('./TermsModal'))

/**
 * The whole of onboarding: one screen, one optional modal, then the feed. Replaces the
 * old welcome + community pledge pair and the link-out to the Intercom help center.
 */
export default function WelcomeScreen() {
  const strings = Strings.get().onboarding.welcome
  const theme = useTheme()

  const {
    hasAgreedToTerms,
    setHasAgreedToTerms,
    hasConsentedToSMS,
    setHasConsentedToSMS,
    isTermsModalOpen,
    openTermsModal,
    closeTermsModal,
    join,
    isPending,
    error,
  } = useOnboarding()

  const benefits = [
    { Icon: EventsIcon, text: strings.benefits.gatherings },
    { Icon: ChatIcon, text: strings.benefits.recommendations },
    { Icon: MarketplaceIcon, text: strings.benefits.marketplace },
  ]

  return (
    <CobuStack f={1} minHeight="100dvh" bg="$background" ai="center" jc="center" px="$4" py="$6">
      <CobuStack
        w="100%"
        maxWidth={440}
        bg="$backgroundSecondary"
        br="$4"
        bw={1}
        boc="$borderColor"
        px="$6"
        py="$6"
        gap="$5"
      >
        <BrandBadge />

        <CobuStack gap="$2">
          <CobuText variant="heading" fos="$7" fow="bold" lh={40}>
            {strings.headline}
          </CobuText>
          <CobuText variant="body" color="$colorMuted" fos="$3">
            {strings.subhead}
          </CobuText>
        </CobuStack>

        <CobuStack gap="$4">
          {benefits.map(({ Icon, text }) => (
            <CobuStack key={text} layout="horizontal" gap="$3" ai="center">
              <CobuStack style={{ flexShrink: 0 }}>
                <Icon isActive={false} color={theme.iconColor?.val} />
              </CobuStack>
              <CobuText variant="body" fos="$3" f={1}>
                {text}
              </CobuText>
            </CobuStack>
          ))}
        </CobuStack>

        <Separator boc="$borderColor" />

        <CobuText variant="body" color="$colorMuted" fos="$2" lh={22}>
          {strings.conductSummary}
        </CobuText>

        <CobuStack gap="$4">
          {renderCheckbox({
            checked: hasAgreedToTerms,
            onChange: setHasAgreedToTerms,
            label: (
              <CobuText variant="body" fos="$2" lh={22} f={1}>
                {strings.termsConsentPrefix}
                <CobuText
                  variant="body"
                  fos="$2"
                  color="$textLinkColor"
                  textDecorationLine="underline"
                  onPress={openTermsModal}
                >
                  {strings.termsConsentLink}
                </CobuText>
                {strings.termsConsentSuffix}
              </CobuText>
            ),
          })}

          {renderCheckbox({
            checked: hasConsentedToSMS,
            onChange: setHasConsentedToSMS,
            label: (
              <CobuText variant="body" fos="$2" lh={22} f={1}>
                {strings.smsConsent}
              </CobuText>
            ),
          })}
        </CobuStack>

        {error && (
          <CobuText variant="body" color="$error" fos="$2" ta="center">
            {error}
          </CobuText>
        )}

        <CobuButton
          dsVariant="primary"
          title={isPending ? strings.ctaLoading : strings.cta}
          onPress={join}
          disabled={!hasAgreedToTerms || isPending}
          h="$12"
        />
      </CobuStack>

      {isTermsModalOpen && (
        <Suspense fallback={null}>
          <TermsModal open={isTermsModalOpen} onClose={closeTermsModal} />
        </Suspense>
      )}
    </CobuStack>
  )

  function renderCheckbox({
    checked,
    onChange,
    label,
  }: {
    checked: boolean
    onChange: (next: boolean) => void
    label: React.ReactNode
  }) {
    return (
      <CobuStack layout="horizontal" gap="$3" ai="flex-start">
        <Checkbox
          checked={checked}
          onCheckedChange={(next) => onChange(next === true)}
          // Matches the checkbox size the screens this replaced used; Tamagui's own
          // size scale runs small enough at $4 to be an awkward tap target.
          size="$8"
          mt="$1"
          bw={checked ? 0 : 2}
          bc={checked ? '$black' : 'transparent'}
          boc={checked ? '$black' : '$borderColorLight'}
          style={{ flexShrink: 0 }}
        >
          <Checkbox.Indicator>
            <CheckIcon style={{ color: theme.white?.val, fontSize: 16 }} />
          </Checkbox.Indicator>
        </Checkbox>
        {label}
      </CobuStack>
    )
  }
}
